// Create or edit a task type (docs/03 "TaskType"): slug, name, description, icon, sort order and
// whether users see it. The slug follows the name on a new type until it is edited by hand.
import { type FormEvent, useState } from "react";
import { Button, Input, Switch, Textarea } from "rizzui";

import { useT } from "../i18n";
import { Hint, SelectField, SideDrawer } from "../ui/SideDrawer";
import { ErrorState } from "../ui/states";
import { iconOptions } from "./iconNames";
import { slugify, slugProblem } from "./slug";
import type { TaskType, TaskTypeInput } from "./types";

interface Props {
  open: boolean;
  /** Absent for a new task type. */
  editing?: TaskType;
  onClose: () => void;
  onSave: (input: TaskTypeInput) => Promise<void>;
}

function initial(t: TaskType | undefined): TaskTypeInput {
  return t
    ? { slug: t.slug, name: t.name, description: t.description, icon: t.icon ?? null, sort_order: t.sort_order, enabled: t.enabled }
    : { slug: "", name: "", description: "", icon: null, sort_order: 0, enabled: true };
}

export function TaskTypeDrawer({ open, editing, onClose, onSave }: Props) {
  const t = useT().catalog.taskTypes;
  return (
    <SideDrawer open={open} onClose={onClose} title={editing ? t.edit(editing.name) : t.new}>
      {open ? <TaskTypeForm key={editing?.id ?? "new"} editing={editing} onClose={onClose} onSave={onSave} /> : null}
    </SideDrawer>
  );
}

function TaskTypeForm({ editing, onClose, onSave }: Omit<Props, "open">) {
  const { catalog, common } = useT();
  const t = catalog.taskTypes;
  const f = catalog.form;
  const [form, setForm] = useState(() => initial(editing));
  const [slugTouched, setSlugTouched] = useState(editing !== undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const set = (patch: Partial<TaskTypeInput>) => setForm((f) => ({ ...f, ...patch }));
  const badSlug = form.slug ? slugProblem(form.slug) : undefined;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-5" onSubmit={(e) => void submit(e)} aria-label={t.form}>
      <Input
        label={f.name}
        value={form.name}
        required
        onChange={(e) => set({ name: e.target.value, ...(slugTouched ? {} : { slug: slugify(e.target.value) }) })}
      />
      <div>
        <Input
          label={f.slug}
          value={form.slug}
          required
          error={badSlug}
          onChange={(e) => {
            setSlugTouched(true);
            set({ slug: e.target.value });
          }}
        />
        {badSlug ? null : <Hint>{t.slugHint}</Hint>}
      </div>
      <Textarea label={f.description} value={form.description} rows={3} onChange={(e) => set({ description: e.target.value })} />
      <div className="grid grid-cols-2 gap-4">
        <SelectField label={f.icon} value={form.icon ?? ""} options={iconOptions(f.noIcon)} onChange={(v) => set({ icon: v || null })} />
        <Input
          label={t.sortOrder}
          type="number"
          value={String(form.sort_order ?? 0)}
          onChange={(e) => set({ sort_order: Number.parseInt(e.target.value, 10) || 0 })}
        />
      </div>
      <div>
        <Switch role="switch" label={t.enabled} checked={form.enabled ?? true} onChange={(e) => set({ enabled: e.target.checked })} />
        <Hint>{t.enabledHint}</Hint>
      </div>
      {error ? <ErrorState title={f.notSaved} message={error} /> : null}
      <div className="flex justify-end gap-3 border-t border-muted pt-4">
        <Button variant="outline" onClick={onClose} disabled={busy}>
          {common.actions.cancel}
        </Button>
        <Button type="submit" isLoading={busy} disabled={!!badSlug}>
          {editing ? common.actions.save : t.create}
        </Button>
      </div>
    </form>
  );
}
